import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  Collapse,
  Typography,
  IconButton
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  DirectionsBike as ScooterIcon,
  Security as SafetyIcon,
  SupportAgent as SupportIcon,
  Settings as SettingsIcon,
  People as UsersIcon,
  BarChart as StatsIcon,
  Schedule as ScheduleIcon,
  Event as AppointmentsIcon,
  Description as ConsentIcon,
  Assignment as SurveyIcon,
  ExpandLess,
  ExpandMore,
  ChevronLeft,
  ChevronRight 
} from '@mui/icons-material';
import LogoutIcon from '@mui/icons-material/Logout';
import { deepPurple } from '@mui/material/colors';
import { useAuth } from '../services/auth/AuthContext';

const Sidebar = ({ open, setOpen }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [expanded, setExpanded] = useState({
    support: false,
    management: false
  });

  const toggleExpand = (section) => {
    setExpanded(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navItems = [
    {
      text: 'Dashboard',
      icon: <DashboardIcon />,
      path: '/dashboard'
    },
    {
      text: 'User Management',
      icon: <UsersIcon />,
      subItems: [
        { text: 'Create Users', path: '/createusers' },
        { text: 'User Statistics', path: '/userstats' },
      ]
    },
    {
      text: 'Scooter Management',
      icon: <ScooterIcon />,
      subItems: [
        { text: 'Rental', path: '/rental' },
        { text: 'Scooter Statistics', path: '/scooterstats' }
      ]
    },
    {
      text: 'Support',
      icon: <SupportIcon />,
      subItems: [
        { text: 'Support Center', path: '/support' },
        { text: 'New Ticket', path: '/support/new' }
      ]
    },
    {
      text: 'Safety Precautions',
      icon: <SafetyIcon />,
      path: '/precautions'
    },
    {
      text: 'Consent Forms',
      icon: <ConsentIcon />,
      path: '/consent-forms'
    },
    {
      text: 'Scheduled Appointments',
      icon: <AppointmentsIcon />,
      path: '/appointments'
    },
    {
      text: 'Surveys',
      icon: <SurveyIcon />,
      path: '/surveys'
    },
    {
      text: 'My Schedule',
      icon: <ScheduleIcon />,
      path: '/schedule'
    },
    {
      text: 'Settings',
      icon: <SettingsIcon />,
      path: '/settings'
    },
    {
      text: 'Logout',
      icon: <LogoutIcon />,
      action: handleLogout
    }
  ];

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={() => setOpen(false)}
      sx={{
        width: 240,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: 240,
          boxSizing: 'border-box',
          bgcolor: deepPurple[50],
        },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1, height: 64 }}>
        <IconButton onClick={() => setOpen(false)}>
          <ChevronLeft />
        </IconButton>
      </Box>
      <Divider />

      <List>
        {navItems.map((item) => (
          <React.Fragment key={item.text}>
            {item.subItems ? (
              <>
                <ListItemButton
                  onClick={() => toggleExpand(item.text.toLowerCase().replace(' ', ''))}
                  sx={{
                    '&:hover': {
                      bgcolor: deepPurple[100],
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText primary={item.text} />
                  {expanded[item.text.toLowerCase().replace(' ', '')] ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
                <Collapse
                  in={expanded[item.text.toLowerCase().replace(' ', '')]}
                  timeout="auto"
                  unmountOnExit
                >
                  <List component="div" disablePadding>
                    {item.subItems.map((subItem) => (
                      <ListItem
                        key={subItem.text}
                        disablePadding
                        component={Link}
                        to={subItem.path}
                        sx={{
                          '&.Mui-selected': {
                            bgcolor: deepPurple[100],
                            borderLeft: `4px solid ${deepPurple[500]}`,
                          },
                          '&.Mui-selected:hover': {
                            bgcolor: deepPurple[200],
                          },
                        }}
                        selected={location.pathname === subItem.path}
                      >
                        <ListItemButton sx={{ pl: 4 }}>
                          <ListItemText primary={subItem.text} sx={{ '& span': { fontSize: '0.875rem' } }} />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              </>
            ) : item.action ? (
              <ListItem disablePadding>
                <ListItemButton onClick={item.action}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItemButton>
              </ListItem>
            ) : (
              <ListItem
                disablePadding
                component={Link}
                to={item.path}
                sx={{
                  '&.Mui-selected': {
                    bgcolor: deepPurple[100],
                    borderLeft: `4px solid ${deepPurple[500]}`,
                  },
                  '&.Mui-selected:hover': {
                    bgcolor: deepPurple[200],
                  },
                }}
                selected={location.pathname === item.path}
              >
                <ListItemButton>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItemButton>
              </ListItem>
            )}
          </React.Fragment>
        ))}
      </List>

      <Divider />
      <Box sx={{ p: 2, mt: 'auto' }}>
        <Typography variant="caption" color="text.secondary">
          ScooterLab v1.0
        </Typography>
      </Box>
    </Drawer>
  );
};

export default Sidebar;